import lodashGet from 'lodash/get';
import startCase from 'lodash/startCase';
import lodashIntersection from 'lodash/intersection';
import {
  MANDATORY_SUPER_PROPERTIES,
  MANDATORY_SUPER_PROPERTIES_ALLOWED_VALUES,
} from '@adsk/bim360-analytics-sdk-js';
import {
  assigneeType,
  emptyField,
  screenType,
  workflowTypesMapper,
  workflowRolesOrderedByPriority,
  superPropsFieldsMapping,
} from '../constants/analytics';
import { HQ_USER_TYPE, HQ_COMPANY_TYPE, HQ_ROLE_TYPE } from '../constants/hq-types';
import { ENV_CONFIGS } from '../constants/envs';
import { viewTypes } from '../constants/view-types';
import {
  WORKFLOWS_ROLE_SUBCONTRACTOR,
  WORKFLOWS_ROLE_GENERAL_CONTRACTOR,
  WORKFLOWS_ROLE_CONSTRUCTION_MANAGER,
  WORKFLOWS_ROLE_ARCHITECT,
} from '../constants/roles-types';
import {
  STATUS_ANSWERED,
  STATUS_CLOSED,
  STATUS_REJECTED,
  STATUS_SUBMITTED,
  STATUS_ANSWERED_MANAGER,
  STATUS_REJECTED_MANAGER,
} from '../constants/status-types';
/* General analytics configuration for analyticsSDK */
export const staticMandatorySuperProperties = (region, env, language) => {
  return {
    [MANDATORY_SUPER_PROPERTIES.PLATFORM]: MANDATORY_SUPER_PROPERTIES_ALLOWED_VALUES.PLATFORM.WEB,
    [MANDATORY_SUPER_PROPERTIES.ENVIRONMENT]: ENV_CONFIGS[region][env].ANALYTICS.ENV,
    [MANDATORY_SUPER_PROPERTIES.DATA_CENTER]: region,
    [MANDATORY_SUPER_PROPERTIES.FEATURE_MODULE]: 'Project Management',
    [MANDATORY_SUPER_PROPERTIES.USED_LANGUAGE_CODE]: language,
  };
};

export const isAutomationUser = (mail) => {
  const automationUserNamesList = [
    'bimqapmauto',
    'bim360auto',
    'bim360applausea',
    'bim360mobile',
    '@for-each.com',
  ];

  return automationUserNamesList.some((string) => mail.includes(string));
};

export const booleanToYesNo = (bool) => {
  return bool ? 'Yes' : 'No';
};

export const getAnalyticsMandatoryPeopleProps = (user) => {
  return {
    firstName: user.firstName,
    lastName: user.lastName,
    userMail: user.email,
    isEmployee: isAutomationUser(user.email),
    oxygenId: user.oxygenId,
  };
};

export const viewTypeToScreenType = (viewType) => {
  switch (viewType) {
    case viewTypes.PROJECT:
      return screenType.PROJECT;
    case viewTypes.DOCUMENT:
      return screenType.PANEL;
    default:
      return null;
  }
};

export const getUserRole = (userWorkflowRoles, isAdmin) => {
  // User can be assigned to several roles, we only need the one with the highest priority (according to the priority map for analytics)
  const mainRole = lodashIntersection(workflowRolesOrderedByPriority, userWorkflowRoles)[0];

  switch (mainRole) {
    case WORKFLOWS_ROLE_GENERAL_CONTRACTOR:
      return 'Manager';
    case WORKFLOWS_ROLE_ARCHITECT:
    case WORKFLOWS_ROLE_CONSTRUCTION_MANAGER:
      return 'Reviewer';
    case WORKFLOWS_ROLE_SUBCONTRACTOR:
      return 'Creator';
    default:
      return isAdmin ? 'Project Admin' : 'Viewer';
  }
};

export const getFileNameExtension = (fileName) => {
  if (!fileName) {
    return emptyField;
  }

  const fileNameArray = fileName.split('.');
  if (fileNameArray.length === 1) {
    // Return empty string if we can't extract the extension from the filename
    return '';
  }

  return fileNameArray.pop().toUpperCase();
};

export const registerAnalyticsSuperPropsHelper = (currentProject, region, hostedBy = '') => {
  return {
    [MANDATORY_SUPER_PROPERTIES.ACCOUNT_ID]:
      lodashGet(currentProject, 'account.id') || lodashGet(currentProject, 'accountId'),
    [MANDATORY_SUPER_PROPERTIES.ACCOUNT_NAME]:
      lodashGet(currentProject, 'account.name') || lodashGet(currentProject, 'accountName'),
    [MANDATORY_SUPER_PROPERTIES.PROJECT_ID]: currentProject.id,
    [MANDATORY_SUPER_PROPERTIES.PROJECT_NAME]: currentProject.name,
    [MANDATORY_SUPER_PROPERTIES.DATA_CENTER]: region,
    [superPropsFieldsMapping.HOSTED_BY]: hostedBy,
  };
};

export const getAssigneeTypeByAssigneeId = (
  currentAssigneeId,
  currentUserId,
  oxygen,
  assignedToType
) => {
  if (currentAssigneeId && assignedToType) {
    const currentUserCompanyId = lodashGet(oxygen, `users[${currentUserId}].company.oxygenId`);
    // eslint-disable-next-line default-case
    switch (assignedToType) {
      case HQ_USER_TYPE:
        if (currentUserId === currentAssigneeId) {
          return assigneeType.ME;
        }
        if (
          lodashGet(oxygen, `users[${currentAssigneeId}].company.oxygenId`) === currentUserCompanyId
        ) {
          return assigneeType.MY_COMPANY;
        }

        return assigneeType.USER;
      case HQ_COMPANY_TYPE:
        if (currentUserCompanyId === currentAssigneeId) {
          return assigneeType.MY_COMPANY;
        }

        return assigneeType.COMPANY;
      case HQ_ROLE_TYPE: {
        const userRolesIds = lodashGet(oxygen, `users[${currentUserId}].roles`, []).map(
          (role) => role.oxygenId
        );
        if (userRolesIds.includes(currentAssigneeId)) {
          return assigneeType.MY_ROLE;
        }

        return assigneeType.ROLE;
      }
    }
    return assigneeType.OTHER;
  }
  return assigneeType.UNASSIGNED;
};

export const viewRFI = (document, status, origin, assignee) => {
  const extension = getFileNameExtension(lodashGet(document, 'fileName'));
  const statusInStartCase = status ? startCase(status) : emptyField;

  const properties = {
    Extension: extension,
    Status: statusInStartCase,
    Origin: origin,
    Assignee: assignee,
  };

  return {
    eventName: 'View RFI',
    properties,
  };
};

export const createIssue = (
  coReviewersCount,
  distributionMemberCount,
  document,
  status,
  origin,
  issuePushpin,
  uploadingAttachments,
  workflowType,
  isCreateAnother,
  rfiCustomIdentifierData,
  timeToCreateRFI,
  {
    costImpact,
    scheduleImpact,
    priority,
    category,
    discipline,
    reference,
    description,
    suggestedAnswer,
  }
) => {
  const pushpin = booleanToYesNo(issuePushpin);
  const extension = getFileNameExtension(lodashGet(document, 'fileName'));
  const statusInStartCase = status ? startCase(status) : emptyField;
  const attachmentsCount = uploadingAttachments ? uploadingAttachments.length : emptyField;

  const properties = {
    'Number Of Co-reviewers': coReviewersCount,
    'Number Of Distribution Member': distributionMemberCount,
    Extension: extension,
    Status: statusInStartCase,
    Origin: origin || screenType.OTHER,
    Pushpin: pushpin,
    Attachments: attachmentsCount,
    'Workflow Type': workflowTypesMapper[workflowType] || workflowTypesMapper.NONE,
    'Create Another': isCreateAnother,
    'Cost Impact': costImpact && costImpact.toUpperCase(),
    'Schedule Impact': scheduleImpact && scheduleImpact.toUpperCase(),
    Priority: priority && priority.toUpperCase(),
    Category: category && category.map((i) => i.toUpperCase()),
    Discipline: discipline && discipline.map((i) => i.toUpperCase()),
    'External ID': reference && reference.toUpperCase(),
    Question: booleanToYesNo(!!description),
    'Suggested Answer': booleanToYesNo(!!suggestedAnswer),
    'Time To Create RFI': timeToCreateRFI,
  };

  if (rfiCustomIdentifierData) {
    const {
      isEdited,
      nextAvailableNumberClickedCount,
      validationErrorShownCount,
    } = rfiCustomIdentifierData;
    properties['Number Field Edited'] = booleanToYesNo(isEdited);
    properties['Next Available Number Clicked'] = nextAvailableNumberClickedCount;
    properties['Number Duplication Error Shown'] = validationErrorShownCount;
  }

  return {
    eventName: 'Create RFI',
    properties,
  };
};

export const entityIssueScreen = (origin) => {
  return {
    eventName: 'RFIs Screen',
    properties: {
      Origin: origin,
    },
  };
};

export const addComment = (isMention) => {
  const properties = {
    Mention: booleanToYesNo(isMention),
  };

  return {
    eventName: 'Add RFI Comment',
    properties,
  };
};

export const addAttachment = (attachmentType, fileName, origin) => {
  const properties = {
    'Attachment Type': attachmentType,
    Extension: getFileNameExtension(fileName),
    Origin: origin,
  };

  return {
    eventName: 'Add RFI Attachment',
    properties,
  };
};

export const fieldEdit = (fieldName, State, ChangedBy, isErrorMessageShown) => {
  const properties = {
    'Changed Attribute': startCase(fieldName),
    State: startCase(State),
    'Changed By': ChangedBy,
    'Error Message Shown': booleanToYesNo(isErrorMessageShown),
  };

  return {
    eventName: 'Edit RFI',
    properties,
  };
};

export const statusChange = (
  oldStatus,
  newStatus,
  isChangingAssigneeOnStatusChange,
  isAddingCommentOnStatusChange,
  rfiCustomIdentifierData,
  workflowType,
  showCreateRevisionField
) => {
  const oldStatusInStartCase = oldStatus ? startCase(oldStatus) : emptyField;
  const newStatusInStartCase = newStatus ? startCase(newStatus) : emptyField;

  const isReopen =
    oldStatus === STATUS_CLOSED &&
    (newStatus === STATUS_ANSWERED ||
      newStatus === STATUS_REJECTED ||
      newStatus === STATUS_SUBMITTED ||
      newStatus === STATUS_ANSWERED_MANAGER ||
      newStatus === STATUS_REJECTED_MANAGER);

  const properties = {
    'Previous Status': oldStatusInStartCase,
    Status: newStatusInStartCase,
    'Change Assignee': booleanToYesNo(isChangingAssigneeOnStatusChange),
    'Add Comment': booleanToYesNo(isAddingCommentOnStatusChange),
  };

  if (isReopen) {
    properties['Is Revision'] = showCreateRevisionField;
  }
  if (rfiCustomIdentifierData) {
    properties['Number Field Edited'] = booleanToYesNo(rfiCustomIdentifierData.isEdited);
    properties['Next Available Number Clicked'] =
      rfiCustomIdentifierData.nextAvailableNumberClickedCount;
    properties['Number Duplication Error Shown'] =
      rfiCustomIdentifierData.validationErrorShownCount;
  }

  if (workflowType) {
    properties.Worfkflow = workflowType === 'EU' ? 'B' : 'A';
  }

  return {
    eventName: 'Change RFI Status',
    properties,
  };
};

export const assigneeChange = (prevAssignee, newAssignee) => {
  const properties = {
    Assignee: newAssignee,
    'Previous Assignee': prevAssignee,
  };

  return {
    eventName: 'Change RFI Assignee',
    properties,
  };
};

export const downloadReportAnalytics = (downloadedBy, assignee, origin, status) => {
  const properties = {
    Assignee: assignee,
    Origin: origin,
    Status: startCase(status),
    'Downloaded By': downloadedBy,
  };

  return {
    eventName: 'Download Rfi',
    properties,
  };
};

export const downloadReportDelayed = (attachmentsCount, maxAllowedGenerationTimeInMS) => {
  return {
    eventName: 'Rfi Report Delayed',
    properties: {
      'Num. Of Attachments': attachmentsCount,
      'Max Allowed Report Generation Time (Seconds)': Math.ceil(
        maxAllowedGenerationTimeInMS / 1000
      ),
    },
  };
};

export const downloadReportReadyForDownload = (
  reportGenerationTimeInMS,
  maxAllowedGenerationTimeInMS
) => {
  return {
    eventName: 'Rfi Report Ready For Download',
    properties: {
      'Report Generation Time (Seconds)': Math.ceil(reportGenerationTimeInMS / 1000),
      'Max Allowed Report Generation Time (Seconds)': Math.ceil(
        maxAllowedGenerationTimeInMS / 1000
      ),
    },
  };
};

export const togglePushpinVisibility = (isHiding, assignee = '') => {
  return {
    eventName: isHiding ? 'Hide Rfi' : 'Unhide Rfi',
    properties: {
      Assignee: assignee,
    },
  };
};

export const deleteAttachment = (viewType, assignee, isOwner, status, deletedBy) => {
  const statusInStartCase = status ? startCase(status) : emptyField;
  const origin = viewTypeToScreenType(viewType);

  const properties = {
    Assignee: assignee,
    Origin: origin,
    Status: statusInStartCase,
    'Own Attachment': booleanToYesNo(isOwner),
    'Deleted By': deletedBy,
  };

  return {
    eventName: 'Delete RFI Attachment',
    properties,
  };
};

export const exportList = (reportFormat, viewType) => ({
  eventName: 'Export RFI List',
  properties: {
    Format: reportFormat,
    Origin: viewTypeToScreenType(viewType),
  },
});

export const searchRfi = () => ({
  eventName: 'Search RFI',
});

export const showFieldColumnInTable = (field) => {
  return {
    eventName: 'Add Field To List',
    properties: {
      Field: field,
    },
  };
};

export const hideFieldColumnInTable = (field) => {
  return {
    eventName: 'Remove Field From List',
    properties: {
      Field: field,
    },
  };
};

export const linkReference = (type, error, newOrExisting) => {
  const properties = {
    Type: type,
    Error: error,
    'New Or Existing': newOrExisting,
  };

  return {
    eventName: 'Link Reference',
    properties,
  };
};

export const unlinkReference = (type) => {
  const properties = {
    Type: type,
  };

  return {
    eventName: 'Unlink Reference',
    properties,
  };
};
export const viewRfiClose = (id, status, timeInSeconds) => {
  const properties = {
    'RFI GUID': id,
    'RFI Status': startCase(status),
    'Time To View RFI': timeInSeconds,
  };

  return {
    eventName: 'View RFI - Close',
    properties,
  };
};

export const viewRfiTabSwitch = (id, status, tab) => {
  const properties = {
    'RFI GUID': id,
    'RFI Status': startCase(status),
    'Tab Name': tab,
  };

  return {
    eventName: 'View RFI - Tab Switch',
    properties,
  };
};
